// Centralized API types - no more `any` types!

export interface BlogPost {
  id?: string | number;
  slug: string;
  title: string;
  description: string;
  date: string;
  author?: string;
  category:
    | "ux-case-studies"
    | "digital-marketing"
    | "art-direction"
    | "creative-works"
    | "odds-and-ends";
  tags: string[];
  thumbnailImage?: string;
  hoverImage?: string;
  content?: string;
  excerpt?: string;
  // Storage reference fields
  thumbnail_image?: string;
  hover_image?: string;
  // NDA protection
  is_nda?: boolean;
  // E-book pages (replaces PDF system)
  page_image_urls?: string[];
  // E-book cover (optional)
  ebookCoverImage?: string;
  ebook_cover_image?: string;
  // E-book display settings
  ebook_view_mode?:
    | "single"
    | "double-landscape"
    | "double-portrait";
  ebook_content_type?: "landscape" | "portrait";
}

export interface BlogPostVersion {
  filename: string;
  timestamp: string;
  auto: boolean;
}

// Homepage Settings Types
export interface UXCaseStudiesSettings {
  enabled: boolean;
  title: string;
  columns?: number; // Legacy field - no longer used (fixed 2x2 layout)
  rows?: number; // Legacy field - no longer used (fixed 2x2 layout)
  maxItems: number;
  featuredPosts: string[];
}

export interface DigitalMarketingSettings {
  enabled: boolean;
  title: string;
  featuredTags: string[];
  featuredPosts: string[];
}

export interface ArtDirectionSettings {
  enabled: boolean;
  title: string;
  maxItems: number;
  featuredPosts: string[];
}

export interface CreativeWorksSettings {
  enabled: boolean;
  title: string;
  columns: number;
  rows: number;
  maxItems: number;
  username: string;
}

export interface OddsAndEndsSettings {
  enabled: boolean;
  title: string;
  maxItems: number;
  featuredPosts: string[];
}

export interface HomepageSettings {
  uxCaseStudies: UXCaseStudiesSettings;
  digitalMarketing: DigitalMarketingSettings;
  artDirection: ArtDirectionSettings;
  creativeWorks: CreativeWorksSettings;
  oddsAndEnds: OddsAndEndsSettings;
  recommendations?: {
    enabled: boolean;
    title: string;
    subtitle?: string;
    maxItems: number;
    autoSlide: boolean;
    slideInterval: number;
    items: Array<{
      id: string;
      content: string;
      author: string;
      position: string;
      company: string;
      avatar?: string;
      link?: string;
    }>;
  };
  globalNdaPassword?: string;
  globalNdaPasswordEncrypted?: string | null;
  globalNdaPasswordExpiresAt?: string | null;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PostsResponse {
  posts: BlogPost[];
}

export interface HomepageSettingsResponse {
  settings: HomepageSettings;
}

// Error Types
export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

// Loading States
export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

// Upload Types
export interface UploadResponse {
  success: boolean;
  filename?: string;
  url?: string;
  originalUrl?: string;
  bucket?: string;
  message?: string;
  error?: string;
  requestId?: string;
  uploadTime?: number;
  warnings?: Array<{
    type: string;
    message: string;
    suggestions: string[];
  }>;
}

import { PostId } from "@/lib/storage-keys";

export interface FileUploadData {
  file: File;
  type: "thumbnail" | "hover" | "content" | "ebook";
  postId: PostId; // REQUIRED: Post ID for organized storage
  slug?: never; // PROHIBITED: Use postId instead
}

// ============================================================================
// RESULT TYPE for explicit error handling
// ============================================================================

export type Result<T, E = Error> =
  | { success: true; data: T }
  | { success: false; error: E };

// Domain-specific errors

export class AccessDeniedError extends Error {
  constructor(message: string, public resourceId: string) {
    super(message);
    this.name = "AccessDeniedError";
  }
}
